import { z } from 'zod'

// URL validation - allows empty/null but validates if present
const urlSchema = z.string().url('Please enter a valid URL').optional().nullable()

/**
 * Validates a URL field value, returning an error message if invalid.
 * Empty/null values are allowed (field is optional).
 */
export function validateUrl(value: string | null | undefined): string | undefined {
  if (!value || value.trim() === '')
    return undefined // Empty is valid (becomes null)
  const result = urlSchema.safeParse(value.trim())
  if (!result.success) {
    return result.error.issues[0]?.message || 'Invalid URL'
  }
  return undefined
}

export function normalizeUrlStrict(input: string): string {
  const trimmed = input.trim()
  if (trimmed === '')
    return ''

  // Ensure a URL object can be constructed, trying schemaless as https
  let url: URL | undefined
  try {
    url = new URL(trimmed)
  }
  catch {
    try {
      url = new URL(`https://${trimmed}`)
    }
    catch {
      return '' // invalid
    }
  }

  if (!/^https?:$/i.test(url.protocol)) {
    return '' // only http/https allowed
  }

  // Upgrade to https if not already (user preference: reduce duplicates, safer default)
  const protocol = 'https:'

  // Canonicalize hostname: lowercase and drop leading www.
  let hostname = url.hostname.toLowerCase()
  if (hostname.startsWith('www.'))
    hostname = hostname.slice(4)

  // Drop default ports
  let port = url.port
  if (port === '80' || port === '443')
    port = ''

  // Keep path and query; strip trailing slash only if root
  let path = url.pathname
  const search = url.search
  if (path === '/' && search === '') {
    path = ''
  }

  // Rebuild canonical URL without hash fragments
  const origin
    = port === ''
      ? `${protocol}//${hostname}`
      : `${protocol}//${hostname}:${port}`
  return `${origin}${path}${search}`
}

/**
 * Validates that a redirect URL is a safe internal path.
 * Prevents open redirect attacks by rejecting absolute URLs, protocol-relative
 * URLs, and other bypass techniques.
 *
 * @param url - The redirect URL to validate
 * @returns The URL if it's a safe relative path, '/' otherwise
 */
export function isSafeRedirect(url: string | undefined): string {
  if (url === undefined || url === '')
    return '/'

  // Normalize backslashes to forward slashes to prevent bypass via \/evil.com
  const normalized = url.replaceAll('\\', '/')

  // Must start with a single forward slash (relative path)
  // Reject protocol-relative URLs (//evil.com), absolute URLs, and other schemes
  if (!normalized.startsWith('/') || normalized.startsWith('//'))
    return '/'

  // Reject URLs containing control characters
  for (let i = 0; i < normalized.length; i++) {
    if (normalized.codePointAt(i)! < 0x20)
      return '/'
  }

  return normalized
}

/**
 * Sanitizes a URL by validating it and only allowing safe schemes (http and https)
 * @param url - The URL string to sanitize
 * @returns The original URL if safe, undefined otherwise
 */
export function sanitizeUrl(url: string | undefined): string | undefined {
  if (url === undefined || url === '')
    return undefined
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? url
      : undefined
  }
  catch {
    return undefined
  }
}
